import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Upload, FileText, Search, Eye, EyeOff, Calendar, FolderOpen, Plus, X } from 'lucide-react';
import Pagination from '../Common/Pagination';
import DatePicker from '../Common/DatePicker';
import AttachmentPreviewModal from '../Common/AttachmentPreviewModal';
import { employeeService } from '../../services/api';
import toast from 'react-hot-toast';
import {
  maxDate,
  resolveCompanyMinDate,
} from '../../utils/selectableDateBounds';

const PAYSLIP_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'other', label: 'Other' },
];

export default function Payslips({
  employees,
  darkMode,
  hasActiveFilters = false,
  selectedMonthYear: selectedMonthYearProp,
  onMonthYearChange,
}) {
  const payslipMinDate = React.useMemo(() => {
    const fiveYearsAgo = new Date(new Date().getFullYear() - 5, 0, 1);
    return maxDate(resolveCompanyMinDate(), fiveYearsAgo) || fiveYearsAgo;
  }, []);

  // For DatePicker we store the raw range ("yyyy-mm-dd/yyyy-mm-dd") separately
  const [selectedMonthRange, setSelectedMonthRange] = useState('');
  // Display/API key: use controlled prop when provided, otherwise internal state
  const [internalMonthYear, setInternalMonthYear] = useState('');
  const selectedMonthYear = selectedMonthYearProp !== undefined ? selectedMonthYearProp : internalMonthYear;
  const setSelectedMonthYear = useCallback((val) => {
    if (typeof onMonthYearChange === 'function') {
      onMonthYearChange(val);
    } else {
      setInternalMonthYear(val);
    }
  }, [onMonthYearChange]);
  const [uploadedPayslips, setUploadedPayslips] = useState({});
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showFolderUploadModal, setShowFolderUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkUploadStep, setBulkUploadStep] = useState('select'); // 'select' | 'confirm'
  const [validationResult, setValidationResult] = useState(null); // { accepted: [...], rejected: [...] }
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [uploadMonthYear, setUploadMonthYear] = useState('');
  const [showPerEmployeeUploadModal, setShowPerEmployeeUploadModal] = useState(false);
  const [perEmployeeUploadEmployee, setPerEmployeeUploadEmployee] = useState(null);
  const [perEmployeeUploadType, setPerEmployeeUploadType] = useState('standard');
  const [perEmployeeUploadFile, setPerEmployeeUploadFile] = useState(null);
  const perEmployeeFileInputRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: null,
    blobUrlToRevoke: null,
  });
  // Persistent upload result/errors (like import employees panel, not toast)
  const [uploadResult, setUploadResult] = useState(null);

  // Payslip bulk upload lock: always locked, unlock via passkey each time (resets after each upload)
  const [unlockedForMonthYear, setUnlockedForMonthYear] = useState(null);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [unlockPasskey, setUnlockPasskey] = useState('');
  const [passkeyError, setPasskeyError] = useState('');
  const [unlockingBusy, setUnlockingBusy] = useState(false);
  const [showPasskey, setShowPasskey] = useState(false);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  // Parse "December-2025" to { month: 12, year: 2025 }
  const parseMonthYear = (monthYearStr) => {
    if (!monthYearStr) return null;
    const [monthLabelRaw, yearStrRaw] = String(monthYearStr).split('-') || [];
    const monthLabel = (monthLabelRaw || '').trim();
    const yearStr = (yearStrRaw || '').trim();
    const monthIndex = monthNames.findIndex(m => m.toLowerCase() === (monthLabel || '').toLowerCase());
    const year = parseInt(yearStr || '', 10);
    if (monthIndex < 0 || isNaN(year)) return null;
    return { month: monthIndex + 1, year };
  };

  const monthYearToRangeValue = useCallback((monthYearLabel) => {
    const parsed = parseMonthYear(monthYearLabel);
    if (!parsed) return '';
    const yyyy = parsed.year;
    const mm = String(parsed.month).padStart(2, '0');
    const lastDay = new Date(yyyy, parsed.month, 0).getDate();
    return `${yyyy}-${mm}-01/${yyyy}-${mm}-${String(lastDay).padStart(2, '0')}`;
  }, []);

  // Keep DatePicker value synced when month-year is controlled from parent.
  useEffect(() => {
    if (!selectedMonthYear) {
      setSelectedMonthRange('');
      return;
    }
    const nextRange = monthYearToRangeValue(selectedMonthYear);
    if (nextRange && nextRange !== selectedMonthRange) {
      setSelectedMonthRange(nextRange);
    }
  }, [selectedMonthYear, monthYearToRangeValue, selectedMonthRange]);

  const handleUnlockWithPasskey = useCallback(async () => {
    if (!unlockPasskey.trim()) {
      setPasskeyError('Please enter the passkey');
      toast.error('Please enter the passkey');
      return;
    }
    if (!selectedMonthYear) return;
    const parsed = parseMonthYear(selectedMonthYear);
    if (!parsed) return;

    try {
      setUnlockingBusy(true);
      setPasskeyError('');
      await employeeService.unlockPayslips(parsed.month, parsed.year, { passkey: unlockPasskey.trim() });
      setUnlockedForMonthYear(selectedMonthYear);
      setShowPasskeyModal(false);
      setUnlockPasskey('');
      setShowPasskey(false);
      setPasskeyError('');
      toast.success(`Payslip bulk upload unlocked for ${selectedMonthYear}`);
      setShowBulkUploadModal(true);
      setBulkUploadStep('select');
      setValidationResult(null);
    } catch (err) {
      console.error('Payslip unlock failed:', err);
      setPasskeyError(err?.response?.status === 429 ? 'Please wait before trying again' : 'Wrong passkey');
      toast.error(err?.response?.data?.message || err?.message || 'Invalid passkey');
    } finally {
      setUnlockingBusy(false);
    }
  }, [unlockPasskey, selectedMonthYear]);

  const openBulkUploadFlow = useCallback(() => {
    if (!selectedMonthYear) {
      toast.error('Please select a month-year first');
      return;
    }
    const isUnlocked = unlockedForMonthYear === selectedMonthYear;
    if (isUnlocked) {
      setShowBulkUploadModal(true);
      setBulkUploadStep('select');
      setValidationResult(null);
    } else {
      setShowPasskeyModal(true);
    }
  }, [selectedMonthYear, unlockedForMonthYear]);

  // Zip filename: ${companyCode}-payslips-${month}-${year} e.g. pss-payslips-dec-2025
  const getBulkDownloadZipFilename = () => {
    const companyData = typeof localStorage !== 'undefined' ? localStorage.getItem('companyData') : null;
    let companyCode = 'company';
    if (companyData) {
      try {
        const company = JSON.parse(companyData);
        companyCode = (company.code || company.companyCode || company.company_code || '')
          .toString()
          .trim()
          .toLowerCase() || companyCode;
        if (companyCode === 'company' && company.name) {
          companyCode = company.name.replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase().slice(0, 10) || 'company';
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (!selectedMonthYear) return `${companyCode}-payslips.zip`;
    const [monthLabel, yearStr] = selectedMonthYear.split('-');
    const year = (yearStr || new Date().getFullYear()).trim();
    const monthIndex = monthNames.findIndex(m => m.toLowerCase() === (monthLabel || '').toLowerCase());
    const month = monthIndex >= 0 ? monthShort[monthIndex] : 'month';
    return `${companyCode}-payslips-${month}-${year}.zip`;
  };

  // Derive relieving date similar to Payroll Investment Declaration screen
  const getEmployeeRelievingDate = (employee) => {
    if (!employee) return null;

    const status = employee.employmentStatus?.toLowerCase();

    if (status === 'terminated') {
      return (
        employee.terminationDate ||
        employee.lastWorkingDay ||
        employee.endDate ||
        employee.separationDate ||
        employee.dateOfLeaving ||
        employee.relievedDate
      );
    }

    if (status === 'resigned') {
      return (
        employee.resignationDate ||
        employee.dateOfLeaving ||
        employee.relievedDate ||
        employee.lastWorkingDay
      );
    }

    if (status === 'relieved') {
      return (
        employee.relievedDate ||
        employee.dateOfLeaving ||
        employee.lastWorkingDay ||
        employee.endDate
      );
    }

    return (
      employee.dateOfLeaving ||
      employee.relievedDate ||
      employee.leaveDate ||
      employee.resignationDate ||
      employee.terminationDate ||
      employee.exitDate ||
      employee.lastWorkingDay ||
      employee.endDate ||
      employee.separationDate
    );
  };

  // Filter employees who were present at any time in the selected month range
  const getFilteredEmployees = () => {
    if (!selectedMonthRange) return [];

    const [fromStr, toStr] = selectedMonthRange.split('/') || [];
    if (!fromStr || !toStr) return [];

    const rangeStart = new Date(fromStr);
    const rangeEnd = new Date(toStr);
    if (isNaN(rangeStart) || isNaN(rangeEnd)) return [];

    return employees.filter((employee) => {
      const joinStr =
        employee.dateOfJoin ||
        employee.joiningDate ||
        employee.joinDate ||
        employee.dateOfJoining ||
        null;

      const joinDate = joinStr ? new Date(joinStr) : null;
      const relievingStr = getEmployeeRelievingDate(employee);
      const relievedDate = relievingStr ? new Date(relievingStr) : null;

      if (!joinDate || isNaN(joinDate)) return false;

      // Employee is considered present if they joined on/before the end of the range
      // and have no relieving date yet or were relieved on/after the start of the range.
      const joinedBeforeOrInRange = joinDate <= rangeEnd;
      const stillEmployedOrRelievedAfterStart = !relievedDate || relievedDate >= rangeStart;

      return joinedBeforeOrInRange && stillEmployedOrRelievedAfterStart;
    });
  };

  const filteredEmployees = getFilteredEmployees();

  // Apply search filter
  const getSearchedEmployees = () => {
    if (!searchTerm) return filteredEmployees;
    
    const searchLower = searchTerm.toLowerCase();
    return filteredEmployees.filter(employee => {
      const empCode = employee.empCode?.toLowerCase() || '';
      const name = (employee.user?.name || employee.name || '').toLowerCase();
      const mobile = (employee.user?.phone || employee.phone || '').toLowerCase();
      const email = (employee.user?.email || employee.email || '').toLowerCase();
      
      return empCode.includes(searchLower) || 
             name.includes(searchLower) || 
             mobile.includes(searchLower) || 
             email.includes(searchLower);
    });
  };

  const searchedEmployees = getSearchedEmployees();

  // Pagination: slice searchedEmployees based on itemsPerPage and currentPage
  const totalItems = searchedEmployees.length;
  const perPage = itemsPerPage === 'all' ? totalItems : (parseInt(itemsPerPage, 10) || 10);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.max(1, Math.ceil(totalItems / perPage));
  const startIndex = (currentPage - 1) * perPage;
  const paginatedEmployees = itemsPerPage === 'all'
    ? searchedEmployees
    : searchedEmployees.slice(startIndex, startIndex + perPage);

  // Reset to page 1 when filters or itemsPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonthYear, itemsPerPage]);

  // Load uploaded payslips for the selected month-year
  useEffect(() => {
    if (selectedMonthYear) {
      loadUploadedPayslips();
    }
  }, [selectedMonthYear]);

  const loadUploadedPayslips = async () => {
    try {
      const response = await employeeService.getPayslips({ monthYear: selectedMonthYear });
      if (response.success) {
        const payslipsMap = {};
        response.data.forEach(payslip => {
          payslipsMap[payslip.employeeId] = payslip;
        });
        setUploadedPayslips(payslipsMap);
      }
    } catch (error) {
      console.error('Error loading payslips:', error);
    }
  };

  // File format: Emp_name(Emp_code).pdf — no space before parenthesis (e.g. "P Kavin (PSS0492)" rejected, "P Kavin(PSS0492)" accepted)
  const PAYSLIP_FILENAME_REGEX = /^(.+)\((.+)\)$/;
  const PAYSLIP_FORMAT_HINT = 'Employee Name(Employee Code).pdf';
  const normalizeEmployeeName = (value) =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

  // Validate bulk files: returns { accepted: [{ file, employeeName, employeeCode, employee }], rejected: [{ file, reason }] }
  const validateBulkPayslipFiles = (files) => {
    const accepted = [];
    const rejected = [];
    const list = Array.isArray(files) ? files : Array.from(files || []);

    list.forEach((file) => {
      if (file.type !== 'application/pdf') {
        rejected.push({ file, reason: 'Not a PDF file' });
        return;
      }

      const fileName = file.name.replace(/\.pdf$/i, '');
      const match = fileName.match(PAYSLIP_FILENAME_REGEX);

      if (!match) {
        rejected.push({ file, reason: `Invalid format. Use "${PAYSLIP_FORMAT_HINT}" (no space before parenthesis)` });
        return;
      }

      // Reject if there is a space (or whitespace) immediately before the opening parenthesis
      const namePart = match[1];
      if (/\s$/.test(namePart)) {
        rejected.push({ file, reason: 'Space before parenthesis not allowed. Use Name(Code).pdf' });
        return;
      }

      const employeeName = namePart.trim();
      const employeeCode = match[2].trim();

      const codeMatches = employees
        .filter((emp) => String(emp.empCode || '').trim() === employeeCode)
        .sort((a, b) => {
          const aJoin = a?.joiningDate ? new Date(a.joiningDate).getTime() : 0;
          const bJoin = b?.joiningDate ? new Date(b.joiningDate).getTime() : 0;
          if (aJoin !== bJoin) return bJoin - aJoin;
          return (b?.id || 0) - (a?.id || 0);
        });

      let employee = codeMatches[0] || null;
      if (employee) {
        const actualName = normalizeEmployeeName(employee.user?.name || employee.name);
        if (actualName !== normalizeEmployeeName(employeeName)) {
          rejected.push({ file, reason: 'Employee name/code mismatch in file name' });
          return;
        }
      } else {
        const nameMatches = employees.filter(
          (emp) => normalizeEmployeeName(emp.user?.name || emp.name) === normalizeEmployeeName(employeeName)
        );
        if (nameMatches.length === 1) {
          employee = nameMatches[0];
        } else if (nameMatches.length > 1) {
          rejected.push({ file, reason: 'Multiple employees found for same name. Use the correct employee code.' });
          return;
        }
      }

      if (!employee) {
        rejected.push({ file, reason: 'Employee not found' });
        return;
      }

      accepted.push({ file, employeeName, employeeCode, employee });
    });

    return { accepted, rejected };
  };

  const handleBulkFileSelect = (event) => {
    const files = event.target.files;
    if (!files?.length) return;
    const result = validateBulkPayslipFiles(files);
    setValidationResult(result);
    setBulkUploadStep('confirm');
    event.target.value = '';
  };

  // Folder upload modal: validate and keep only accepted as selectedFiles (rejected are skipped)
  const handleFolderSelect = (event) => {
    const files = event.target.files;
    if (!files?.length) return;
    const result = validateBulkPayslipFiles(files);
    if (result.rejected.length > 0) {
      result.rejected.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`);
      });
    }
    setSelectedFiles(result.accepted.map((a) => ({ file: a.file, employeeName: a.employeeName, employeeCode: a.employeeCode })));
    event.target.value = '';
  };

  const handleBulkUploadConfirm = async () => {
    if (!validationResult?.accepted?.length || !selectedMonthYear) {
      toast.error('No accepted files to upload');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('monthYear', selectedMonthYear);
      validationResult.accepted.forEach((item) => {
        formData.append('payslips', item.file);
      });

      const response = await employeeService.uploadBulkPayslips(formData);
      const data = response?.data ?? response;
      const acceptedList = data?.accepted ?? [];
      const rejectedList = data?.rejected ?? [];
      const successCount = data?.acceptedCount ?? acceptedList.length;

      await loadUploadedPayslips();
      const errors = rejectedList.map((r) => `${r.fileName}: ${r.reason}`);
      const payslipsRes = await employeeService.getPayslips({ monthYear: selectedMonthYear });
      const uploadedIds = new Set(
        (payslipsRes?.data ?? payslipsRes)?.map((p) => p.employeeId) ?? []
      );
      const missingList = filteredEmployees
        .filter((emp) => !uploadedIds.has(emp.id))
        .map((emp) => `${emp.user?.name || emp.name} (${emp.empCode})`);

      setUploadResult({
        successCount,
        errors,
        missingList,
      });
      if (successCount > 0) {
        toast.success(`${successCount} payslip(s) uploaded successfully`);
      }

      setShowBulkUploadModal(false);
      setBulkUploadStep('select');
      setValidationResult(null);
      setUnlockedForMonthYear(null); // Lock again after each bulk upload
    } catch (error) {
      console.error('Bulk upload error:', error);
      const msg = error?.response?.data?.message || error?.message || 'Failed to upload payslips';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderUploadPayslips = async () => {
    if (!selectedFiles.length || !uploadMonthYear) {
      toast.error('Please select files and month-year');
      return;
    }

    // Validate that upload month-year matches selected month-year
    if (uploadMonthYear !== selectedMonthYear) {
      toast.error(`Upload month-year (${uploadMonthYear}) must match selected month-year (${selectedMonthYear})`);
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];
    const uploadedEmployeeIds = new Set();

    try {
      for (const fileData of selectedFiles) {
        try {
          // Find employee by name or code
          const employee = employees.find(emp => 
            (emp.user?.name || emp.name) === fileData.employeeName || 
            emp.empCode === fileData.employeeCode
          );

          if (!employee) {
            errors.push(`Employee not found for file: ${fileData.file.name}`);
            errorCount++;
            continue;
          }

          const formData = new FormData();
          formData.append('payslip', fileData.file);
          formData.append('employeeId', employee.id);
          formData.append('monthYear', uploadMonthYear);

          const response = await employeeService.uploadPayslip(formData);
          if (response.success) {
            setUploadedPayslips(prev => ({
              ...prev,
              [employee.id]: response.data
            }));
            uploadedEmployeeIds.add(employee.id);
            successCount++;
          } else {
            errors.push(`Failed to upload ${fileData.file.name}`);
            errorCount++;
          }
        } catch (error) {
          console.error(`Error uploading ${fileData.file.name}:`, error);
          errors.push(`Error uploading ${fileData.file.name}: ${error.message}`);
          errorCount++;
        }
      }

      // Check for missing employees
      const missingEmployees = filteredEmployees.filter(emp => !uploadedEmployeeIds.has(emp.id));
      const missingList = missingEmployees.map(emp =>
        `${emp.user?.name || emp.name} (${emp.empCode})`
      );
      setUploadResult({
        successCount,
        errors,
        missingList,
      });
      if (successCount > 0) {
        toast.success(`${successCount} payslip(s) uploaded successfully`);
      }

      setShowFolderUploadModal(false);
      setSelectedFiles([]);
      setUploadMonthYear('');
    } catch (error) {
      console.error('Folder upload error:', error);
      toast.error('Failed to upload payslips');
    } finally {
      setLoading(false);
    }
  };

  const getPayslipTypeForEmployee = (employeeId) => {
    const entry = uploadedPayslips[employeeId];
    const doc = entry?.documents?.find(d => d.type === 'standard') || entry?.documents?.[0];
    return doc?.type || 'standard';
  };

  const handleViewPayslip = async (employeeId) => {
    const toastId = toast.loading('Generating payslip PDF… please wait');
    try {
      const type = getPayslipTypeForEmployee(employeeId);
      const data = await employeeService.downloadPayslip(employeeId, selectedMonthYear, type);
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const employee = employees.find(emp => emp.id === employeeId);
      const fileName = `${employee?.user?.name || employee?.name || 'Employee'}(${employee?.empCode || employeeId})-${selectedMonthYear}.pdf`;
      setPreviewState({
        isOpen: true,
        url,
        downloadUrl: url,
        fileName,
        blobUrlToRevoke: url,
      });
      toast.success('Payslip ready', { id: toastId });
    } catch (error) {
      console.error('View error:', error);
      toast.error(error?.response?.data?.message || 'Failed to view payslip', { id: toastId });
    }
  };

  const handleDownloadPayslip = async (employeeId) => {
    const toastId = toast.loading('Preparing payslip download… please wait');
    try {
      const type = getPayslipTypeForEmployee(employeeId);
      const data = await employeeService.downloadPayslip(employeeId, selectedMonthYear, type);
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const employee = employees.find(emp => emp.id === employeeId);
      const fileName = `${employee?.empCode || employeeId}-${selectedMonthYear}.pdf`;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download payslip', { id: toastId });
    }
  };

  const closePreview = () => {
    if (previewState.blobUrlToRevoke) {
      window.URL.revokeObjectURL(previewState.blobUrlToRevoke);
    }
    setPreviewState({
      isOpen: false,
      url: null,
      downloadUrl: null,
      fileName: null,
      blobUrlToRevoke: null,
    });
  };

  const handleBulkDownload = async () => {
    if (!selectedMonthYear) {
      toast.error('Please select a month-year');
      return;
    }

    const toastId = toast.loading('Preparing payslip ZIP… please wait');
    try {
      const response = await employeeService.downloadBulkPayslips({
        monthYear: selectedMonthYear,
        employeeIds: searchedEmployees.map(emp => emp.id),
      });

      const data = response.data;
      // Backend may return 200 with JSON { success: false, message } when no documents found
      const isJsonBlob = data instanceof Blob && data.type && data.type.toLowerCase().includes('application/json');
      if (isJsonBlob) {
        const text = await data.text();
        try {
          const json = JSON.parse(text);
          if (json && json.success === false && json.message) {
            toast.error(json.message || 'No payslip documents found', { id: toastId });
            return;
          }
        } catch (_) {
          /* not JSON */
        }
      }

      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getBulkDownloadZipFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started', { id: toastId });
    } catch (error) {
      console.error('Bulk download error:', error);
      const msg = error?.response?.data;
      if (msg instanceof Blob && msg.type && String(msg.type).toLowerCase().includes('application/json')) {
        try {
          const text = await msg.text();
          const json = JSON.parse(text);
          toast.error(json?.message || 'Failed to download bulk payslips', { id: toastId });
        } catch (_) {
          toast.error('Failed to download bulk payslips', { id: toastId });
        }
      } else if (typeof msg === 'object' && msg?.message) {
        toast.error(msg.message, { id: toastId });
      } else {
        toast.error('Failed to download bulk payslips', { id: toastId });
      }
    }
  };

  const handleMonthYearChange = (e) => {
    const value = e.target.value; // "yyyy-mm-dd/yyyy-mm-dd"
    setSelectedMonthRange(value);

    const [fromStr] = value.split('/') || [];
    const match = String(fromStr || '').match(/^(\d{4})-(\d{2})-\d{2}$/);
    if (match) {
      const yyyy = Number(match[1]);
      const monthIdx = Number(match[2]) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        const label = `${monthNames[monthIdx]}-${yyyy}`;
        setSelectedMonthYear(label);
        setUnlockedForMonthYear(null); // Reset unlock when month changes
      }
    }
  };

  const openPerEmployeeUpload = (employee) => {
    setPerEmployeeUploadEmployee(employee);
    setPerEmployeeUploadType('standard');
    setPerEmployeeUploadFile(null);
    setShowPerEmployeeUploadModal(true);
  };

  const handlePerEmployeeFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    setPerEmployeeUploadFile(file);
    e.target.value = '';
  };

  const handlePerEmployeeUploadSubmit = async () => {
    if (!perEmployeeUploadEmployee || !perEmployeeUploadFile || !selectedMonthYear) {
      toast.error('Please select a file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('payslip', perEmployeeUploadFile);
      formData.append('employeeId', perEmployeeUploadEmployee.id);
      formData.append('monthYear', selectedMonthYear);
      formData.append('type', perEmployeeUploadType);
      const response = await employeeService.uploadPayslip(formData);
      if (response.success) {
        setUploadedPayslips(prev => {
          const empId = perEmployeeUploadEmployee.id;
          const existing = prev[empId];
          const docs = existing?.documents ? [...existing.documents] : [];
          const newDoc = {
            id: response.data?.id,
            type: response.data?.type ?? perEmployeeUploadType,
            fileName: response.data?.fileName,
            filePath: response.data?.filePath,
          };
          const idx = docs.findIndex(d => d.type === (response.data?.type ?? perEmployeeUploadType));
          if (idx >= 0) docs[idx] = newDoc;
          else docs.push(newDoc);
          return { ...prev, [empId]: { employeeId: empId, documents: docs } };
        });
        toast.success('Payslip uploaded successfully');
        setShowPerEmployeeUploadModal(false);
        setPerEmployeeUploadEmployee(null);
        setPerEmployeeUploadFile(null);
      } else {
        toast.error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Per-employee upload error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload payslip');
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryDoc = (empId) => {
    const entry = uploadedPayslips[empId];
    if (!entry?.documents?.length) return null;
    return entry.documents.find(d => d.type === 'standard') || entry.documents[0];
  };

  return (
    <div className={`${darkMode ? 'bg-[#2C2C2C]' : 'bg-white'} rounded-none p-4 shadow-sm`}>
      <input
        ref={perEmployeeFileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handlePerEmployeeFileChange}
      />
      {/* Month-Year Selection and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2 z-50">
            <div className="min-w-[220px]">
              <DatePicker
                value={selectedMonthRange}
                onChange={handleMonthYearChange}
                darkMode={darkMode}
                label="Select Month Year"
                monthYearEmptyLabel="Select Month Year"
                min={payslipMinDate}
                max={new Date()}
                monthYearOnly
                showMonthName = {true}
              />
            </div>
          </div>
          
          {selectedMonthYear && (
            <span className={`text-sm ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-600'}`}>
              {searchedEmployees.length} employee(s) found
            </span>
          )}
        </div>
        
        {selectedMonthYear && (
          <div className="flex space-x-2">
            <button
              onClick={openBulkUploadFlow}
              className={`flex items-center px-4 py-2 rounded-none font-medium transition-colors ${
                darkMode 
                  ? 'bg-[#03DAC6] text-[#1C1C1C] hover:bg-[#00BFA5]' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Upload size={16} className="mr-2" />
              Bulk Upload
            </button>
            {/* <button
              onClick={() => setShowFolderUploadModal(true)}
              className={`flex items-center px-4 py-2 rounded-none font-medium transition-colors ${
                darkMode 
                  ? 'bg-[#BB86FC] text-white hover:bg-[#A370E0]' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <FolderOpen size={16} className="mr-2" />
              Upload Folder
            </button> */}
            <button
              onClick={handleBulkDownload}
              className={`flex items-center px-4 py-2 rounded-none font-medium transition-colors ${
                darkMode 
                  ? 'bg-[#FF6B6B] text-white hover:bg-[#FF5252]' 
                  : 'rh-btn-primary hover:opacity-95'
              }`}
            >
              <Download size={16} className="mr-2" />
              Bulk Download
            </button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      {selectedMonthYear && (
        <div className="mb-4">
          <div className="relative">
            <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-400'}`} />
            <input
              type="text"
              name="employee_filter_query"
              id="admin-payslips-employee-filter"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-lpignore="true"
              placeholder="Search by employee code, name, or mobile number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 ${
                darkMode 
                  ? 'bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0] placeholder-[#A6A9C8]' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
      </div>
      )}

      {/* Employee Table */}
      {selectedMonthYear ? (
        <div className="flex flex-col">
          {/* Show rows per page & table container */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <label className={`text-sm font-medium ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                Show:
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={`px-2 py-1.5 border rounded-none text-sm ${
                  darkMode
                    ? 'bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-[#BB86FC]'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-redhalo-red'
                } focus:outline-none`}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
            </div>
            <span className={`text-sm ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-600'}`}>
              {totalItems} employee(s) found
            </span>
          </div>
          <div
            className={`${
              darkMode ? 'rh-data-table-shell-dark' : 'rh-data-table-shell'
            } overflow-hidden ${
              searchedEmployees.length > 0 && totalPages > 1
                ? 'rounded-b-none border-b-0'
                : 'rounded-b-table-lg'
            }`}
          >
            <div className="max-h-[500px] overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead
              className={`sticky top-0 z-10 ${
                darkMode ? 'rh-table-header-row-dark' : 'rh-table-header-row'
              }`}
            >
              <tr>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Sl. No
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Employee Code
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Name
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Department
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Designation
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Date of Join
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Date of Relieving
                </th>
                <th className="px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Employment Status
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-bold uppercase tracking-wide">
                  Actions
                </th>
          </tr>
        </thead>
            <tbody>
              {paginatedEmployees.map((employee, index) => {
                const hasPayslip = uploadedPayslips[employee.id];
                const serialNo = itemsPerPage === 'all' ? index + 1 : startIndex + index + 1;
                const zebra =
                  (startIndex + index) % 2 === 0
                    ? darkMode
                      ? 'bg-[#252525]'
                      : 'bg-gray-100/80'
                    : darkMode
                    ? 'bg-[#1f1f1f]'
                    : 'bg-[#fafafa]';
                return (
                  <tr
                    key={employee.id}
                    className={`border-b transition-colors ${
                      darkMode
                        ? `border-[#4C4C4C] ${zebra} hover:bg-[#333333]`
                        : `border-gray-300 ${zebra} hover:bg-redhalo-mint/40`
                    }`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {serialNo}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {employee.empCode || 'N/A'}
                    </td>
                    <td className="min-w-[140px] whitespace-nowrap px-4 py-3 text-sm text-gray-800 dark:text-gray-100" title={employee.user?.name || employee.name || 'N/A'}>
                      {employee.user?.name || employee.name || 'N/A'}
                    </td>
                    <td className="min-w-[120px] whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300" title={employee.department?.name || 'N/A'}>
                      {employee.department?.name || 'N/A'}
                    </td>
                    <td className="min-w-[120px] whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300" title={employee.designation?.name || 'N/A'}>
                      {employee.designation?.name || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {employee.relievedDate ? new Date(employee.relievedDate).toLocaleDateString() : 
                       employee.resignationDate ? new Date(employee.resignationDate).toLocaleDateString() :
                       employee.terminationDate ? new Date(employee.terminationDate).toLocaleDateString() : 'N/A'}
                    </td>
              <td className="px-4 py-3 whitespace-nowrap min-w-[7rem]">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        employee.employmentStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
                        employee.employmentStatus === 'probation' ? 'bg-yellow-100 text-yellow-800' :
                        employee.employmentStatus === 'resigned' ? 'bg-orange-100 text-orange-800' :
                        employee.employmentStatus === 'relieved' ? 'bg-red-100 text-red-800' :
                        employee.employmentStatus === 'terminated' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {employee.employmentStatus || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-1 flex-nowrap justify-start">
                        {hasPayslip ? (
                          <>
                            <button
                              onClick={() => handleViewPayslip(employee.id)}
                              title="View payslip"
                              className={`inline-flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${
                                darkMode 
                                  ? 'border-[#03DAC6] bg-[#2C2C2C] hover:bg-[#3C3C3C] text-[#03DAC6]' 
                                  : 'border-redhalo-teal/80 bg-white hover:bg-redhalo-mint/50 text-redhalo-navy'
                              }`}
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDownloadPayslip(employee.id)}
                              title="Download payslip"
                              className={`inline-flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${
                                darkMode 
                                  ? 'border-[#03DAC6] bg-[#2C2C2C] hover:bg-[#3C3C3C] text-[#03DAC6]' 
                                  : 'border-redhalo-teal/80 bg-white hover:bg-redhalo-mint/50 text-redhalo-navy'
                              }`}
                            >
                              <Download size={18} />
                            </button>
                          </>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            darkMode ? 'bg-[#4C4C4C] text-[#A6A9C8]' : 'bg-red-100 text-red-800'
                          }`}>
                            No Payslip
                          </span>
                        )}
                      </div>
                    </td>
            </tr>
                );
              })}
        </tbody>
      </table>
            </div>
          </div>
          
          {searchedEmployees.length === 0 && (
            <div className={`text-center py-8 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
              {searchTerm ? 'No employees found matching your search.' : 'No employees found for the selected month-year.'}
            </div>
          )}

          {searchedEmployees.length > 0 && totalPages > 1 && (
            <Pagination
              darkMode={darkMode}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              className="-mt-px rounded-t-none border border-t-0 border-gray-200 dark:border-[#4C4C4C]"
            />
          )}

          {/* Totals: Filtered + cards (right-aligned, rounded like Employee table) */}
          {searchedEmployees.length > 0 && selectedMonthYear && (
            <div className={`flex justify-end items-center flex-wrap gap-2 px-4 py-3 border-t mt-2 ${
              darkMode ? 'border-gray-700 bg-[#2C2C2C]' : 'border-gray-200 bg-gray-50'
            }`}>
              {hasActiveFilters && (
                <div className={`px-2.5 py-1 rounded-lg text-sm font-semibold ${
                  darkMode ? 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]' : 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]'
                }`}>
                  Filtered
                </div>
              )}
              <div className={`px-2.5 py-1 rounded-lg text-sm font-semibold ${
                darkMode ? 'bg-[#BB86FC] text-[#1C1C1C]' : 'rh-accent-soft'
              }`}>
                Total: {filteredEmployees.length}
              </div>
              <div className={`px-2.5 py-1 rounded-lg text-sm font-semibold ${
                darkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'
              }`}>
                With payslips: {filteredEmployees.filter(emp => uploadedPayslips[emp.id]).length}
              </div>
              <div className={`px-2.5 py-1 rounded-lg text-sm font-semibold ${
                darkMode ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-800'
              }`}>
                Without payslips: {filteredEmployees.filter(emp => !uploadedPayslips[emp.id]).length}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={`text-center py-8 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
          Please select a month-year to view payslips.
        </div>
      )}

      {/* Upload result / error panel – centered (like import employees, not toast) */}
      {uploadResult && (uploadResult.errors?.length > 0 || uploadResult.missingList?.length > 0 || uploadResult.successCount > 0) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg max-h-[85vh] overflow-hidden rounded-lg shadow-xl flex flex-col ${
            darkMode ? 'bg-[#2C2C2C] border border-[#4C4C4C]' : 'bg-white border border-gray-200'
          }`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
              darkMode ? 'border-[#4C4C4C] bg-red-900/20' : 'border-red-200 bg-red-50'
            }`}>
              <span className={`font-semibold ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                Payslip upload – result
              </span>
              <button
                type="button"
                onClick={() => setUploadResult(null)}
                className={`p-1 rounded ${darkMode ? 'hover:bg-[#4C4C4C] text-red-200' : 'hover:bg-red-200 text-red-700'}`}
                aria-label="Close"
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {uploadResult.successCount > 0 && (
                <p className={`text-sm font-medium ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
                  {uploadResult.successCount} payslip(s) uploaded successfully.
                </p>
              )}
              {uploadResult.errors?.length > 0 && (
                <div>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
                    Errors ({uploadResult.errors.length}):
                  </p>
                  <ul className={`text-sm list-disc list-inside space-y-1 ${darkMode ? 'text-red-100' : 'text-red-800'}`}>
                    {uploadResult.errors.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadResult.missingList?.length > 0 && (
                <div>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
                    Missing payslips for:
                  </p>
                  <ul className={`text-sm list-disc list-inside space-y-1 max-h-48 overflow-y-auto ${darkMode ? 'text-red-100' : 'text-red-800'}`}>
                    {uploadResult.missingList.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={`flex justify-end gap-2 px-4 py-3 border-t shrink-0 ${
              darkMode ? 'border-[#4C4C4C] bg-[#2C2C2C]' : 'border-gray-200 bg-gray-50'
            }`}>
              <button
                type="button"
                onClick={() => setUploadResult(null)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  darkMode ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setUploadResult(null)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  darkMode ? 'bg-[#03DAC6] text-[#1C1C1C] hover:bg-[#00BFA5]' : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Passkey Modal – Unlock payslip bulk upload (same passkey as attendance) */}
      {showPasskeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className={`rounded-lg shadow-xl w-96 p-6 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Bulk Upload Payslips</h2>
              <button
                onClick={() => {
                  setShowPasskeyModal(false);
                  setUnlockPasskey('');
                  setShowPasskey(false);
                  setPasskeyError('');
                }}
                className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Enter the passkey to bulk upload payslips for{" "}
                <strong>{selectedMonthYear}</strong>.
              </p>
              <div className="relative">
                <input
                  type={showPasskey ? 'text' : 'password'}
                  value={unlockPasskey}
                  onChange={(e) => {
                    setUnlockPasskey(e.target.value);
                    setPasskeyError('');
                  }}
                  placeholder="Enter passkey"
                  className={`w-full px-3 py-2 pr-10 border rounded ${
                    passkeyError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-redhalo-red'
                        : 'bg-white border-gray-300 text-gray-800 focus:border-redhalo-red'
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && unlockPasskey.trim()) {
                      handleUnlockWithPasskey();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPasskey(!showPasskey)}
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                  tabIndex={-1}
                >
                  {showPasskey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passkeyError && (
                <p className="text-red-500 text-xs mt-1">{passkeyError}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPasskeyModal(false);
                  setUnlockPasskey('');
                  setShowPasskey(false);
                  setPasskeyError('');
                }}
                className={`px-4 py-2 rounded ${
                  darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUnlockWithPasskey}
                disabled={!unlockPasskey.trim() || unlockingBusy}
                className={`px-4 py-2 rounded ${
                  darkMode ? 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:bg-gray-600' : 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:bg-gray-400'
                }`}
              >
                {unlockingBusy ? 'Unlocking...' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal – Step 1: Select files → Step 2: Validate & confirm */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg shadow-xl flex flex-col ${darkMode ? 'bg-[#2C2C2C]' : 'bg-white'}`}>
            <div className="p-6 overflow-y-auto flex-1">
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-900'}`}>
                Bulk Upload Payslips for {selectedMonthYear}
              </h3>

              {bulkUploadStep === 'select' && (
                <>
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                      Select PDF files (multiple). Files will be validated before upload.
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      multiple
                      onChange={handleBulkFileSelect}
                      className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${
                        darkMode
                          ? 'file:bg-[#3C3C3C] file:text-[#E0E0E0] bg-[#3C3C3C] border-[#4C4C4C]'
                          : 'file:bg-gray-100 file:text-gray-700 bg-white border-gray-300'
                      } border rounded-lg`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
                      Format: "{PAYSLIP_FORMAT_HINT}" — no space before parenthesis (e.g. P Kavin(PSS0492).pdf accepted, P Kavin (PSS0492).pdf rejected).
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowBulkUploadModal(false);
                        setBulkUploadStep('select');
                        setValidationResult(null);
                        setUnlockedForMonthYear(null);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        darkMode ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {bulkUploadStep === 'confirm' && validationResult && (
                <>
                  <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-[#3C3C3C]' : 'bg-gray-50'}`}>
                    <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                      Validation result: {validationResult.accepted.length} accepted, {validationResult.rejected.length} rejected.
                    </p>
                    {validationResult.accepted.length > 0 && (
                      <div className="mb-3">
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                          Accepted ({validationResult.accepted.length}):
                        </p>
                        <ul className={`text-xs max-h-32 overflow-y-auto space-y-0.5 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-600'}`}>
                          {validationResult.accepted.map((item, i) => (
                            <li key={i}>{item.file.name} → {item.employeeName}({item.employeeCode})</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {validationResult.rejected.length > 0 && (
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                          Rejected ({validationResult.rejected.length}):
                        </p>
                        <ul className={`text-xs max-h-32 overflow-y-auto space-y-0.5 ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                          {validationResult.rejected.map((item, i) => (
                            <li key={i}>{item.file.name} — {item.reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setBulkUploadStep('select');
                        setValidationResult(null);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        darkMode ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        setShowBulkUploadModal(false);
                        setBulkUploadStep('select');
                        setValidationResult(null);
                        setUnlockedForMonthYear(null);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        darkMode ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkUploadConfirm}
                      disabled={!validationResult.accepted.length || loading}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        darkMode
                          ? 'bg-[#03DAC6] text-[#1C1C1C] hover:bg-[#00BFA5] disabled:bg-[#4C4C4C] disabled:text-[#A6A9C8]'
                          : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500'
                      }`}
                    >
                      {loading ? 'Uploading...' : `Upload ${validationResult.accepted.length} accepted`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Folder Upload Modal */}
      {showFolderUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-[90%] max-w-2xl rounded-lg shadow-xl ${darkMode ? 'bg-[#2C2C2C]' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-900'}`}>
                Upload Payslips Folder
              </h3>
              
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                  Select Month-Year for Upload
                </label>
                <div className="relative">
                  <Calendar size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-400'}`} />
                  <select
                    value={uploadMonthYear}
                    onChange={(e) => setUploadMonthYear(e.target.value)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 ${
                      darkMode 
                        ? 'bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select Month-Year</option>
                    {/* We no longer maintain a separate monthYearOptions list; this select is tied to the
                        currently selected month-year so that uploadMonthYear matches selectedMonthYear. */}
                    {selectedMonthYear && (
                      <option value={selectedMonthYear}>{selectedMonthYear}</option>
                    )}
                  </select>
                </div>
                <p className={`text-xs mt-1 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
                  This must match the selected month-year: {selectedMonthYear}
                </p>
              </div>
              
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                  Select PDF Files from Folder
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFolderSelect}
                  className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${
                    darkMode 
                      ? 'file:bg-[#3C3C3C] file:text-[#E0E0E0] bg-[#3C3C3C] border-[#4C4C4C]' 
                      : 'file:bg-gray-100 file:text-gray-700 bg-white border-gray-300'
                  } border rounded-lg`}
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
                  File format: "{PAYSLIP_FORMAT_HINT}"
                </p>
              </div>
              
              {selectedFiles.length > 0 && (
                <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-[#3C3C3C]' : 'bg-gray-100'}`}>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                    Selected Files ({selectedFiles.length}):
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    {selectedFiles.map((fileData, index) => (
                      <div key={index} className={`text-xs py-1 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-600'}`}>
                        {fileData.file.name} → {fileData.employeeName}({fileData.employeeCode})
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowFolderUploadModal(false);
                    setSelectedFiles([]);
                    setUploadMonthYear('');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode 
                      ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFolderUploadPayslips}
                  disabled={!selectedFiles.length || !uploadMonthYear || loading}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode 
                      ? 'bg-[#BB86FC] text-white hover:bg-[#A370E0] disabled:bg-[#4C4C4C] disabled:text-[#A6A9C8]' 
                      : 'bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300 disabled:text-gray-500'
                  }`}
                >
                  {loading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-employee upload modal (Upload / Replace / Add type) */}
      {showPerEmployeeUploadModal && perEmployeeUploadEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-[90%] max-w-md rounded-lg shadow-xl ${darkMode ? 'bg-[#2C2C2C]' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-900'}`}>
                Upload payslip for {perEmployeeUploadEmployee.user?.name || perEmployeeUploadEmployee.name || perEmployeeUploadEmployee.empCode}
              </h3>
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                  Payslip type
                </label>
                <select
                  value={perEmployeeUploadType}
                  onChange={(e) => setPerEmployeeUploadType(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 ${
                    darkMode ? 'bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  {PAYSLIP_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                  PDF file
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => perEmployeeFileInputRef.current?.click()}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      darkMode ? 'bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {perEmployeeUploadFile ? perEmployeeUploadFile.name : 'Choose file'}
                  </button>
                  {perEmployeeUploadFile && (
                    <span className={`text-xs ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
                      {(perEmployeeUploadFile.size / 1024).toFixed(1)} KB
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-1 ${darkMode ? 'text-[#A6A9C8]' : 'text-gray-500'}`}>
                  Path: employee-payslips / company / {selectedMonthYear} / {String(perEmployeeUploadEmployee.user?.name || perEmployeeUploadEmployee.name || '').replace(/\s+/g, '_')}({perEmployeeUploadEmployee.empCode || ''})
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPerEmployeeUploadModal(false);
                    setPerEmployeeUploadEmployee(null);
                    setPerEmployeeUploadFile(null);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode ? 'bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePerEmployeeUploadSubmit}
                  disabled={!perEmployeeUploadFile || loading}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode 
                      ? 'bg-[#03DAC6] text-[#1C1C1C] hover:bg-[#00BFA5] disabled:bg-[#4C4C4C] disabled:text-[#A6A9C8]' 
                      : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500'
                  }`}
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
        onClose={closePreview}
      />
    </div>
  );
}

